import { Box, DialogContentText, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { tss } from 'tss-react/mui';

import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { Or2Image } from '@/components/image/Or2Image';

import compressFolderWindows from 'Images/zip-upload-instructions/zipupload-compress-windows.png';
import compressFolderMac from 'Images/zip-upload-instructions/zipupload-compress-mac.png';

const useStyles = tss.create(({ theme }) => ({
  image: {
    width: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
  },
}));

const compressInstructions = [
  {
    label: 'Windows',
    src: compressFolderWindows,
    alt: 'Right-click a folder in Windows and choose Compress to ZIP File',
  },
  {
    label: 'Mac',
    src: compressFolderMac,
    alt: 'Right-click a folder on a Mac and choose Compress',
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onRequestUpload: () => void;
}

export const UploadZipDialog = ({
  open,
  onClose,
  onRequestUpload,
  ...other
}: Props) => {
  const { classes } = useStyles();

  const [canUploadFoldersViaDnd, setCanUploadFoldersViaDnd] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCanUploadFoldersViaDnd(
        Boolean(window.DataTransferItem?.prototype?.webkitGetAsEntry),
      );
    }
  }, []);

  return (
    <Or2Dialog
      open={open}
      onClose={onClose}
      title="Upload an entire folder"
      fullScreenThreshold="sm"
      aria-labelledby="upload-zip-dialog-title"
      aria-describedby="upload-zip-dialog-description"
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button onClick={onRequestUpload}>Upload Zip File</Or2Button>
        </>
      }
      {...other}
    >
      <DialogContentText component="div" id="upload-zip-dialog-description">
        {canUploadFoldersViaDnd && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            The easiest way to upload a whole folder is to drag and drop it
            directly into the browser window. If that doesn&apos;t work, you can
            upload a folder as a zip file instead.
          </Typography>
        )}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          How to upload a folder as a zip
        </Typography>
        <Typography
          component="ol"
          variant="body2"
          sx={{ mt: 0, pl: 2.5, '& li': { mb: 1 } }}
        >
          <li>
            On your computer, right-click the folder you want to upload and
            compress it into a zip file
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ my: 1 }}
            >
              {compressInstructions.map(({ label, src, alt }) => (
                <Box key={label} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    display="block"
                    sx={{ mb: 0.5, fontWeight: 'bold' }}
                  >
                    {label}
                  </Typography>
                  <Or2Image
                    className={classes.image}
                    src={src}
                    alt={alt}
                    width={460}
                    unoptimized
                  />
                </Box>
              ))}
            </Stack>
          </li>
          <li>
            Click <strong>Upload Zip File</strong> below and select the zip file
            you just created
          </li>
          <li>
            Your folder and all its contents will be{' '}
            <strong>automatically unpacked</strong> and will appear in your
            documents
          </li>
        </Typography>
      </DialogContentText>
    </Or2Dialog>
  );
};
